import { Fragment, useCallback, useState, useMemo } from "react";
import cls from "classnames";
import { Button, message, Modal, Space, Spin, Popconfirm } from "antd";
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Form,
  FormItem,
  Input,
  Password,
  FormButtonGroup,
  Submit,
} from "@formily/antd";
import { useRequest } from "ahooks";
import { createSchemaField } from "@formily/react";
import { createForm } from "@formily/core";
import { department as departmentService } from "@/services";
const schema = {
  type: "object",
  properties: {
    username: {
      type: "string",
      title: "用户名",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
    },
    name: {
      type: "string",
      title: "姓名",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
    },
    job: {
      type: "string",
      title: "职务",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
    },
    phone: {
      type: "string",
      title: "手机",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
    },
    password: {
      type: "string",
      title: "密码",
      format: "password",
      "x-decorator": "FormItem",
      "x-component": "Password",
      "x-component-props": {
        checkStrength: true,
        visibilityToggle: true,
        placeholder: "不修改密码则留空",
      },
    },
  },
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    Password,
  },
});

function DepartmentUserButton({ token, departmentId, callback }) {
  const [visible, setVisible] = useState(false);
  const [version, setVersion] = useState(Date.now());
  const [selectedId, setSelectedId] = useState();
  const [username, setUsername] = useState();
  const [name, setName] = useState();
  const [job, setJob] = useState();
  const [phone, setPhone] = useState();
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const form = useMemo(
    () =>
      createForm({
        validateFirst: true,
        initialValues: { id: selectedId, username, name, job, phone },
      }),
    [selectedId, username, name, job, phone]
  );
  const clickHandler = useCallback(() => setVisible(true), [setVisible]);
  const resetHandler = useCallback(() => {
    setSelectedId(undefined);
    setUsername(undefined);
    setName(undefined);
    setJob(undefined);
    setPhone(undefined);
  }, [setSelectedId, setUsername, setName, setPhone, setJob]);
  const editHandler = useCallback(
    (id, username, name, job, phone) => {
      setSelectedId(id);
      setUsername(username);
      setName(name);
      setJob(job);
      setPhone(phone);
    },
    [setSelectedId, setUsername, setName, setJob, setPhone]
  );
  const createUser = useCallback(
    (values) => {
      setEditLoading(true);
      departmentService.createUser(departmentId, values, token).then((res) => {
        setEditLoading(false);
        message.success("操作成功");
        setSelectedId(undefined);
        setUsername(undefined);
        setName(undefined);
        setJob(undefined);
        setPhone(undefined);
        setVersion(Date.now());
      });
    },
    [token, departmentId, setSelectedId, setUsername, setVersion]
  );
  const deleteHandler = useCallback(
    (id) => {
      setDeleteLoading(true);
      departmentService.removeUser(departmentId, { id }, token).then((res) => {
        setDeleteLoading(false);
        message.success("操作成功");
        setVersion(Date.now());
      });
    },
    [departmentId, token]
  );
  const { data = [], loading } = useRequest(
    () => {
      return departmentService.listUsers(departmentId, token);
    },
    {
      ready: visible,
      refreshDeps: [departmentId, version],
    }
  );

  return (
    <Fragment>
      <Button size="small" icon={<UserAddOutlined />} onClick={clickHandler} />
      <Modal
        title="账号管理"
        visible={visible}
        footer={null}
        width={800}
        onCancel={() => {
          setVisible(false);
          callback(Date.now());
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div className="modal-depart-user-container">
          <div className="modal-depart-user-list">
            {loading ? (
              <Spin size="small" style={{ margin: "5px auto" }} />
            ) : (
              data.map((e) => (
                <div
                  key={`item-${e.id}`}
                  className={cls("modal-depart-user-item", {
                    active: e.id === selectedId,
                  })}
                >
                  <div className="name">
                    {e.username} {e.is_active === false && "[已删除]"}
                  </div>
                  <div className="toolbar">
                    <Space>
                      <Button
                        disabled={e.is_active === false}
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() =>
                          editHandler(e.id, e.username, e.name, e.job, e.phone)
                        }
                      />
                      <Popconfirm
                        title="请确认是否删除?"
                        onConfirm={() => deleteHandler(e.id)}
                        okText="是"
                        cancelText="否"
                      >
                        <Button
                          disabled={e.is_active === false}
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={deleteLoading}
                        />
                      </Popconfirm>
                    </Space>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="modal-depart-user-form">
            <Form
              form={form}
              labelCol={5}
              wrapperCol={16}
              onAutoSubmit={createUser}
            >
              <SchemaField schema={schema} />
              <FormButtonGroup.FormItem>
                <Submit loading={editLoading}>
                  {selectedId ? "更新" : "新建"}
                </Submit>
                <Button onClick={resetHandler}>清空</Button>
              </FormButtonGroup.FormItem>
            </Form>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
}

export default DepartmentUserButton;
